import React, { useEffect, useState, useMemo } from "react";
import AxiosInstance from "../../../AxiosInstance.jsx";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useUserType } from "../../../../contexts/UserTypeContext.jsx";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  MenuItem,
  FormControl,
  Divider,
  Paper,
  Slide,
  Avatar,
  useMediaQuery,
  useTheme,
  Autocomplete,
} from "@mui/material";
import FormatColorText from "@mui/icons-material/FormatColorText";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SettingsCardHeader, {
  getSettingsCardShellSx,
} from "../../../../components/Setting/SettingsCardHeader.jsx";
import { Col } from "react-bootstrap";
import showToast from "../../../../components/Toast/Toster.jsx";
import BlueButton from "../../../../components/Button/BlueButton.jsx";
import WhiteButton from "../../../../components/Button/WhiteButton.jsx";
import SettingsLayout from "../../../../components/Layouts/SettingsLayout.jsx";
import InputText from "../../../../components/InputFields/InputText.jsx";
import RenderParamsInputText from "../../../../components/InputFields/RenderParamsInputText.jsx";
import moment from "moment-timezone";
import { getItem } from "../../../../utils/storage";
import { clearAuthCache } from "../../../../components/Login/Auth.jsx";

function DateTimeFormat() {
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  
  // State for all three settings
  const [selectedTimezone, setSelectedTimezone] = useState("America/New_York");
  const [selectedDateFormat, setSelectedDateFormat] = useState("MM/DD/YYYY");
  const [selectedTimeFormat, setSelectedTimeFormat] = useState("12h");
  const [customInputValue, setCustomInputValue] = useState("DD/MMM/YYYY");
  const [timezoneSearchInput, setTimezoneSearchInput] = useState("");
  
  // Original values to track changes
  const [originalTimezone, setOriginalTimezone] = useState(null);
  const [originalDateFormat, setOriginalDateFormat] = useState(null);
  const [originalTimeFormat, setOriginalTimeFormat] = useState(null);
  const [originalCustomValue, setOriginalCustomValue] = useState(null);
  
  // Current time for preview
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Responsive breakpoints
  const theme = useTheme();
  const isDarkUi = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const primaryColor = isDonationMode ? "#68b2cb" : "#0f2b46";

  // Fetch all settings on component mount
  useEffect(() => {
    fetchAllSettings();
    
    // Update current time every second for live preview
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Update search input when timezone is initially loaded from API (only once)
  useEffect(() => {
    if (originalTimezone !== null && selectedTimezone && timezoneOptions.length > 0 && !timezoneSearchInput) {
      const option = timezoneOptions.find(opt => opt.value === selectedTimezone);
      if (option) {
        setTimezoneSearchInput(option.label);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalTimezone]);

  const fetchAllSettings = async () => {
    await Promise.all([
      fetchTimezone(),
      fetchDateFormat(),
      fetchTimeFormat(),
    ]);
  };

  // Fetch Timezone
  const fetchTimezone = async () => {
    try {
      const CompanyId = getItem("CompanyId");
      if (!CompanyId || CompanyId === "null" || CompanyId === "undefined") {
        setSelectedTimezone("America/New_York");
        setOriginalTimezone("America/New_York");
        return;
      }

      const response = await AxiosInstance.get(`/v1/user/company-profile/${CompanyId}`, {});
      if (response?.data?.success) {
        const timezone = response?.data?.data?.userProfile?.Timezone || "America/New_York";
        setSelectedTimezone(timezone);
        setOriginalTimezone(timezone);
      } else {
        setSelectedTimezone("America/New_York");
        setOriginalTimezone("America/New_York");
      }
    } catch (error) {
      console.error("Error fetching timezone:", error.message);
      setSelectedTimezone("America/New_York");
      setOriginalTimezone("America/New_York");
    }
  };

  // Fetch Date Format
  const fetchDateFormat = async () => {
    try {
      const CompanyId = getItem("CompanyId");
      const response = await AxiosInstance.get(`/v1/theme/${CompanyId}`, {});
      if (response?.data?.statusCode === 200) {
        const format = response?.data?.data?.Format || "MM/DD/YYYY";
        const predefinedFormats = ["MM/DD/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", "YYYY-MMM-DD"];
        
        if (predefinedFormats.includes(format)) {
          setSelectedDateFormat(format);
          setOriginalDateFormat(format);
          setOriginalCustomValue(null);
        } else {
          setSelectedDateFormat("custom");
          setCustomInputValue(format);
          setOriginalDateFormat("custom");
          setOriginalCustomValue(format);
        }
      } else {
        setSelectedDateFormat("MM/DD/YYYY");
        setOriginalDateFormat("MM/DD/YYYY");
        setOriginalCustomValue(null);
      }
    } catch (error) {
      console.error("Error fetching date format:", error.message);
      setSelectedDateFormat("MM/DD/YYYY");
      setOriginalDateFormat("MM/DD/YYYY");
      setOriginalCustomValue(null);
    }
  };

  // Fetch Time Format
  const fetchTimeFormat = async () => {
    try {
      const CompanyId = getItem("CompanyId");
      const response = await AxiosInstance.get(`/v1/user/time-format/${CompanyId}`, {});
      if (response?.data?.statusCode === 200) {
        const format = response?.data?.data?.TimeFormat || "12h";
        setSelectedTimeFormat(format);
        setOriginalTimeFormat(format);
      } else {
        setSelectedTimeFormat("12h");
        setOriginalTimeFormat("12h");
      }
    } catch (error) {
      console.error("Error fetching time format:", error.message);
      setSelectedTimeFormat("12h");
      setOriginalTimeFormat("12h");
    }
  };

  // Save Timezone
  const setTimezone = async (Timezone, showToastMessage = true) => {
    try {
      const CompanyId = getItem("CompanyId");
      if (!CompanyId || CompanyId === "null" || CompanyId === "undefined") {
        if (showToastMessage) {
          showToast.error("Company ID not found. Please log in again.");
        }
        return;
      }

      const response = await AxiosInstance.put(`/v1/user/update-profile/${CompanyId}`, {
        Timezone,
      });

      if (response?.data?.statusCode === 200) {
        setOriginalTimezone(Timezone);
        clearAuthCache();
        if (showToastMessage) {
          showToast.success("Timezone updated successfully!");
        }
      } else {
        if (showToastMessage) {
          showToast.error("Failed to update timezone.");
        }
      }
    } catch (error) {
      console.error("Error updating timezone:", error?.message);
      if (showToastMessage) {
        showToast.error("Error updating timezone. Please try again.");
      }
    }
  };

  // Save Date Format
  const setDateFormat = async (Format, showToastMessage = true) => {
    try {
      const CompanyId = getItem("CompanyId");
      const response = await AxiosInstance.post(`/v1/theme/date-format`, {
        Format,
        CompanyId,
      });

      if (response?.data?.statusCode === 200) {
        const predefinedFormats = ["MM/DD/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", "YYYY-MMM-DD"];
        if (predefinedFormats.includes(Format)) {
          // Update state for predefined format
          setSelectedDateFormat(Format);
          setOriginalDateFormat(Format);
          setOriginalCustomValue(null);
        } else {
          // Update state for custom format
          setSelectedDateFormat("custom");
          setCustomInputValue(Format);
          setOriginalDateFormat("custom");
          setOriginalCustomValue(Format);
        }
        clearAuthCache();
        if (showToastMessage) {
          showToast.success("Date format updated!");
        }
      } else {
        if (showToastMessage) {
          showToast.error("Failed to update date format.");
        }
      }
    } catch (error) {
      console.error("Error updating date format:", error?.message);
      if (showToastMessage) {
        showToast.error("Error updating date format. Please try again.");
      }
    }
  };

  // Handle custom date format change
  const handleCustomDateChange = (event) => {
    const inputValue = event.target.value;
    const filteredValue = inputValue.replace(/[^a-zA-Z/-]/g, "");
    const upperCaseValue = filteredValue.toUpperCase();
    setCustomInputValue(upperCaseValue);
  };

  // Save Time Format
  const setTimeFormat = async (TimeFormat, showToastMessage = true) => {
    try {
      const CompanyId = getItem("CompanyId");
      const response = await AxiosInstance.put(`/v1/user/time-format/${CompanyId}`, {
        TimeFormat,
      });

      if (response?.data?.statusCode === 200) {
        setOriginalTimeFormat(TimeFormat);
        clearAuthCache();
        if (showToastMessage) {
          showToast.success("Time format updated!");
        }
      } else {
        if (showToastMessage) {
          showToast.error("Failed to update time format.");
        }
      }
    } catch (error) {
      console.error("Error updating time format:", error?.message);
      if (showToastMessage) {
        showToast.error("Error updating time format. Please try again.");
      }
    }
  };

  // Check if there are any changes
  const hasChanges = () => {
    if (originalTimezone === null || originalDateFormat === null || originalTimeFormat === null) {
      return false;
    }

    // Check date format changes (including custom)
    let dateFormatChanged = false;
    if (selectedDateFormat === "custom") {
      if (originalDateFormat === "custom") {
        dateFormatChanged = customInputValue.trim() !== (originalCustomValue || "").trim();
      } else {
        dateFormatChanged = true;
      }
    } else {
      if (originalDateFormat === "custom") {
        dateFormatChanged = true;
      } else {
        dateFormatChanged = selectedDateFormat !== originalDateFormat;
      }
    }

    return (
      selectedTimezone !== originalTimezone ||
      dateFormatChanged ||
      selectedTimeFormat !== originalTimeFormat
    );
  };

  // Check if all settings are at default values
  const isAtDefaults = () => {
    const defaultTimezone = "America/New_York";
    const defaultDateFormat = "MM/DD/YYYY";
    const defaultTimeFormat = "12h";

    return (
      selectedTimezone === defaultTimezone &&
      selectedDateFormat === defaultDateFormat &&
      selectedTimeFormat === defaultTimeFormat
    );
  };

  // Handle Save
  const handleSave = async () => {
    if (!hasChanges()) {
      return;
    }

    try {
      const promises = [];
      
      if (selectedTimezone !== originalTimezone) {
        promises.push(setTimezone(selectedTimezone, false));
      }
      
      // Handle date format save (including custom)
      if (selectedDateFormat === "custom") {
        if (!customInputValue.trim()) {
          showToast.error("Please enter a custom date format");
          return;
        }
        // Always save custom format
        promises.push(setDateFormat(customInputValue, false));
      } else {
        // Always save predefined format (including DD/MM/YYYY)
        promises.push(setDateFormat(selectedDateFormat, false));
      }
      
      if (selectedTimeFormat !== originalTimeFormat) {
        promises.push(setTimeFormat(selectedTimeFormat, false));
      }

      await Promise.all(promises);
      
      // Refresh settings to ensure state is in sync
      await fetchAllSettings();
      
      showToast.success("Settings saved successfully!");
    } catch (error) {
      console.error("Error saving settings:", error);
      showToast.error("Error saving settings. Please try again.");
    }
  };

  // Handle Reset
  const handleReset = async () => {
    try {
      const defaultTimezone = "America/New_York";
      const defaultDateFormat = "MM/DD/YYYY";
      const defaultTimeFormat = "12h";

      // Update local state
      setSelectedTimezone(defaultTimezone);
      setSelectedDateFormat(defaultDateFormat);
      setSelectedTimeFormat(defaultTimeFormat);
      setCustomInputValue("DD/MMM/YYYY");
      
      // Update search input to show the default timezone label
      const defaultOption = timezoneOptions.find(opt => opt.value === defaultTimezone);
      if (defaultOption) {
        setTimezoneSearchInput(defaultOption.label);
      }

      // Save to backend
      await Promise.all([
        setTimezone(defaultTimezone, false),
        setDateFormat(defaultDateFormat, false),
        setTimeFormat(defaultTimeFormat, false),
      ]);

      showToast.success("Settings reset to defaults!");
      await fetchAllSettings();
    } catch (error) {
      console.error("Error resetting settings:", error);
      showToast.error("Error resetting settings. Please try again.");
    }
  };

  // Get UTC offset for timezone
  const getUTCOffset = (tz) => {
    try {
      const offset = moment.tz(tz).utcOffset();
      const hours = Math.floor(Math.abs(offset) / 60);
      const minutes = Math.abs(offset) % 60;
      const sign = offset >= 0 ? "+" : "-";
      return `(UTC${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")})`;
    } catch (error) {
      return "(UTC+00:00)";
    }
  };

  // Get timezone abbreviation
  const getTimezoneAbbr = (tz) => {
    try {
      return moment.tz(tz).zoneAbbr();
    } catch (error) {
      return "";
    }
  };

  // Get all available timezones
  const getAllTimezones = () => {
    const usaTimezones = [
      "America/New_York",
      "America/Chicago",
      "America/Denver",
      "America/Los_Angeles",
      "America/Anchorage",
      "Pacific/Honolulu"
    ];

    const allTimezones = moment.tz.names().map((tz) => {
      const offset = moment.tz(tz).utcOffset();
      const sign = offset >= 0 ? "+" : "-";
      const hours = Math.floor(Math.abs(offset) / 60)
        .toString()
        .padStart(2, "0");
      const minutes = (Math.abs(offset) % 60).toString().padStart(2, "0");
      const tzAbbr = getTimezoneAbbr(tz);
      const label = `(UTC${sign}${hours}:${minutes}) ${tz.replace(/_/g, " ")}${tzAbbr ? ` (${tzAbbr})` : ""}`;
      
      return {
        label,
        value: tz,
        description: tz.replace(/_/g, " ")
      };
    });

    const usaTimezoneOptions = allTimezones.filter(opt => usaTimezones.includes(opt.value));
    const otherTimezoneOptions = allTimezones.filter(opt => !usaTimezones.includes(opt.value));
    
    otherTimezoneOptions.sort((a, b) => {
      const offsetA = moment.tz(a.value).utcOffset();
      const offsetB = moment.tz(b.value).utcOffset();
      if (offsetA !== offsetB) {
        return offsetA - offsetB;
      }
      return a.value.localeCompare(b.value);
    });

    return [...usaTimezoneOptions, ...otherTimezoneOptions];
  };

  // Memoize timezone options to prevent recalculation on every render
  const timezoneOptions = useMemo(() => {
    return getAllTimezones();
  }, []);

  // Filter timezone options based on search input.
  // When input is the full label of the current selection, show all options — otherwise
  // the full string only matches one row and the dropdown looks like a single item.
  const filteredTimezoneOptions = useMemo(() => {
    if (!timezoneSearchInput.trim()) {
      return timezoneOptions;
    }

    const selectedOption = timezoneOptions.find(
      (opt) => opt.value === selectedTimezone
    );
    const inputTrim = timezoneSearchInput.trim();
    if (
      selectedOption &&
      inputTrim === selectedOption.label.trim()
    ) {
      return timezoneOptions;
    }

    const searchTerm = inputTrim.toLowerCase();
    return timezoneOptions.filter((option) => {
      const label = option.label.toLowerCase();
      const description = option.description.toLowerCase();
      const value = option.value.toLowerCase();

      return (
        label.includes(searchTerm) ||
        description.includes(searchTerm) ||
        value.includes(searchTerm)
      );
    });
  }, [timezoneOptions, timezoneSearchInput, selectedTimezone]);

  // Date format options
  const dateFormatOptions = [
    { value: "MM/DD/YYYY", label: "MM/DD/YYYY" },
    { value: "MM/DD/YYYY", label: "MM/DD/YYYY" },
    { value: "YYYY-MM-DD", label: "YYYY-MM-DD" },
    { value: "YYYY-MMM-DD", label: "YYYY-MMM-DD" },
    { value: "custom", label: "Custom Date" },
  ];

  // Time format options
  const timeFormatOptions = [
    { value: "12h", label: "12-hour (AM/PM)" },
    { value: "24h", label: "24-hour format" },
  ];

  // Preview calculations
  const previewDate = useMemo(() => {
    try {
      const date = moment.tz(currentTime, selectedTimezone);
      const formatToUse = selectedDateFormat === "custom" ? customInputValue : selectedDateFormat;
      return date.format(formatToUse);
    } catch (error) {
      const formatToUse = selectedDateFormat === "custom" ? customInputValue : selectedDateFormat;
      return moment(currentTime).format(formatToUse);
    }
  }, [currentTime, selectedTimezone, selectedDateFormat, customInputValue]);

  const previewTime = useMemo(() => {
    try {
      const date = moment.tz(currentTime, selectedTimezone);
      if (selectedTimeFormat === "12h") {
        return date.format("h:mm A");
      } else {
        return date.format("H:mm");
      }
    } catch (error) {
      const date = moment(currentTime);
      if (selectedTimeFormat === "12h") {
        return date.format("h:mm A");
      } else {
        return date.format("H:mm");
      }
    }
  }, [currentTime, selectedTimezone, selectedTimeFormat]);

  const previewDateTime = useMemo(() => {
    try {
      const date = moment.tz(currentTime, selectedTimezone);
      let format = selectedDateFormat === "custom" ? customInputValue : selectedDateFormat;
      if (selectedTimeFormat === "12h") {
        format += " h:mm A";
      } else {
        format += " H:mm";
      }
      return date.format(format);
    } catch (error) {
      const date = moment(currentTime);
      let format = selectedDateFormat === "custom" ? customInputValue : selectedDateFormat;
      if (selectedTimeFormat === "12h") {
        format += " h:mm A";
      } else {
        format += " H:mm";
      }
      return date.format(format);
    }
  }, [currentTime, selectedTimezone, selectedDateFormat, selectedTimeFormat, customInputValue]);

  const previewTimezone = useMemo(() => {
    const option = timezoneOptions.find(opt => opt.value === selectedTimezone);
    return option ? option.value.replace(/_/g, " ") : selectedTimezone;
  }, [selectedTimezone]);

  return (
    <SettingsLayout>
      <Col
        className="col-12 addProductServiceSideLine productRemove"
        style={{
          marginTop: isMobile ? "12px" : "24px",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          flex: "1 1 auto",
          alignSelf: "stretch",
          boxSizing: "border-box",
          position: "relative",
          zIndex: 9,
          overflow: "visible",
        }}
        xl={12}
        lg={12}
        md={12}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            py: { xs: 1, sm: 1.5 },
            px: { xs: 0, sm: 0 },
          }}
        >
          <Card sx={{ ...getSettingsCardShellSx(theme), maxWidth: "none" }}>
            <SettingsCardHeader
              title="Date & Time Format Settings"
              icon={ScheduleIcon}
              centerIconWithTitle
            />

            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Grid container spacing={2}>
                  {/* First Row: Timezone - Full Width */}
                  <Grid size={12}>
                    <Box>
                      <Autocomplete
                        options={filteredTimezoneOptions}
                        filterOptions={(options) => options}
                        value={timezoneOptions.find(opt => opt.value === selectedTimezone) || null}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            setSelectedTimezone(newValue.value);
                            setTimezoneSearchInput(newValue.label);
                          } else {
                            setSelectedTimezone("");
                            setTimezoneSearchInput("");
                          }
                        }}
                        inputValue={timezoneSearchInput}
                        onInputChange={(event, newInputValue, reason) => {
                          // Update search input only when user is typing
                          // Don't update when reason is "reset" (happens on selection) or "clear"
                          if (reason === "input") {
                            setTimezoneSearchInput(newInputValue);
                          }
                        }}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(option, value) =>
                          value == null ? false : option.value === value.value
                        }
                        disableClearable
                        ListboxProps={{
                          sx: {
                            py: 0.5,
                            "& .MuiAutocomplete-option": {
                              minHeight: "auto",
                              py: 0.75,
                              px: 1.5,
                              lineHeight: 1.35,
                              alignItems: "flex-start",
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <RenderParamsInputText
                            params={params}
                            label="Select Timezone"
                            required
                            name="timezone"
                            size="small"
                            fieldHeight="50px"
                            className={
                              isDonationMode
                                ? "datetime-format-autocomplete--donor"
                                : "datetime-format-autocomplete--contractor"
                            }
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box
                            component="li"
                            {...props}
                            key={option.value}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              py: 0.75,
                              px: 1.5,
                              lineHeight: 1.35,
                              minHeight: "auto !important",
                            }}
                          >
                            {option.label}
                          </Box>
                        )}
                        PaperComponent={({ children, ...other }) => (
                          <Paper
                            {...other}
                            sx={{
                              maxHeight: 400,
                              overflow: "auto",
                              width: isMobile ? "calc(100vw - 32px)" : "500px",
                              boxShadow: "var(--shadow-md)",
                            }}
                          >
                            {children}
                          </Paper>
                        )}
                        noOptionsText="No timezone found"
                        sx={{ width: "100%" }}
                      />
                    </Box>
                  </Grid>

                  {/* Second Row: Date Format and Time Format Side by Side */}
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Box>
                      <FormControl fullWidth size="small">
                        <InputText
                          name="dateFormat"
                          label="Select Date Format"
                          required
                          value={selectedDateFormat}
                          onChange={(e) => setSelectedDateFormat(e.target.value)}
                          select
                          SelectProps={{
                            MenuProps: {
                              PaperProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    minHeight: 36,
                                    py: 0.75,
                                    fontSize: "0.875rem",
                                  },
                                },
                              },
                            },
                          }}
                          size="medium"
                          fieldHeight="50px"
                          fullWidth
                          style={{ width: "100%" }}
                        >
                          {dateFormatOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </InputText>
                      </FormControl>
                    </Box>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <Box>
                      <FormControl fullWidth size="small">
                        <InputText
                          name="timeFormat"
                          label="Select Time Format"
                          required
                          value={selectedTimeFormat}
                          onChange={(e) => setSelectedTimeFormat(e.target.value)}
                          select
                          SelectProps={{
                            MenuProps: {
                              PaperProps: {
                                sx: {
                                  "& .MuiMenuItem-root": {
                                    minHeight: 36,
                                    py: 0.75,
                                    fontSize: "0.875rem",
                                  },
                                },
                              },
                            },
                          }}
                          size="medium"
                          fieldHeight="50px"
                          fullWidth
                          style={{ width: "100%" }}
                        >
                          {timeFormatOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </InputText>
                      </FormControl>
                    </Box>
                  </Grid>
                </Grid>

                {/* Custom Date Format Input */}
                {selectedDateFormat === "custom" && (
                  <Slide
                    direction="up"
                    in={selectedDateFormat === "custom"}
                    mountOnEnter
                    unmountOnExit
                  >
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        mt: 3,
                        background: isDonationMode
                          ? "linear-gradient(135deg, #E9F8FC 0%, #f0f9f4 100%)"
                          : "linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%)",
                        border: `1px solid ${isDonationMode ? "#29B3E2" : "var(--amber)"}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 2,
                        }}
                      >
                        <Avatar
                          sx={{ 
                            bgcolor: isDonationMode ? "#29B3E2" : "var(--amber)", 
                            width: 32, 
                            height: 32 
                          }}
                        >
                          <FormatColorText sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ 
                              color: isDonationMode ? "#29B3E2" : "var(--amber)", 
                              fontWeight: 600 
                            }}
                          >
                            Custom Format
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#666" }}
                          >
                            Define your own pattern
                          </Typography>
                        </Box>
                      </Box>

                      <InputText
                        label="Enter format"
                        placeholder="e.g., DD/MMM/YYYY"
                        value={customInputValue}
                        onChange={handleCustomDateChange}
                        fullWidth
                        required
                        fieldHeight="50px"
                        showErrorOnly
                      />
                    </Paper>
                  </Slide>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Preview Section */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor: isDarkUi ? "#252f3f" : "#f5f5f5",
                    borderRadius: 2,
                    mb: 3,
                    border: isDarkUi ? "1px solid #2d3b4e" : "none",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      color: isDarkUi ? "#e8eaed" : "inherit",
                    }}
                  >
                    Preview
                  </Typography>
                  <Grid container spacing={2}>
                    {/* First Row: Timezone (left) and Time (right) */}
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={isDarkUi ? { color: "#b8c5d6" } : undefined}
                      >
                        Timezone:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          ...(isDarkUi ? { color: "#e8eaed" } : {}),
                        }}
                      >
                        {previewTimezone}
                      </Typography>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={isDarkUi ? { color: "#b8c5d6" } : undefined}
                      >
                        Time:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          ...(isDarkUi ? { color: "#e8eaed" } : {}),
                        }}
                      >
                        {previewTime}
                      </Typography>
                    </Grid>
                    {/* Second Row: Date (left) and Date and Time (right) */}
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={isDarkUi ? { color: "#b8c5d6" } : undefined}
                      >
                        Date:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          ...(isDarkUi ? { color: "#e8eaed" } : {}),
                        }}
                      >
                        {previewDate}
                      </Typography>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={isDarkUi ? { color: "#b8c5d6" } : undefined}
                      >
                        Date and Time:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          ...(isDarkUi ? { color: "#e8eaed" } : {}),
                        }}
                      >
                        {previewDateTime}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    justifyContent: { xs: "stretch", sm: "space-between" },
                    alignItems: { xs: "stretch", sm: "center" },
                    flexWrap: "wrap",
                    width: "100%",
                  }}
                >
                  <Box sx={{ width: { xs: "100%", sm: "auto" }, flex: { sm: "0 0 auto" } }}>
                    <WhiteButton
                      onClick={handleReset}
                      variant="outline"
                      type="button"
                      disabled={isAtDefaults()}
                      isDonationMode={isDonationMode}
                      label={
                        <>
                          <RestartAltIcon sx={{ mr: 1, fontSize: 20 }} />
                          Reset
                        </>
                      }
                      style={{
                        borderColor: primaryColor,
                        color: primaryColor,
                        width: isMobile ? "100%" : "auto",
                      }}
                    />
                  </Box>
                  <Box sx={{ width: { xs: "100%", sm: "auto" }, flex: { sm: "0 0 auto" } }}>
                    <BlueButton
                      type="button"
                      disabled={!hasChanges()}
                      isDonationMode={isDonationMode}
                      onClick={handleSave}
                      label="Save"
                      style={{
                        width: isMobile ? "100%" : "auto",
                      }}
                    />
                  </Box>
                </Box>
            </CardContent>
          </Card>
        </Box>
      </Col>
    </SettingsLayout>
  );
}

export default DateTimeFormat;
